import React, { memo, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useTags } from "modules/providers/tags-provider";
import { HASH_KEY, MONTH_DAY_YEAR_FORMAT } from "modules/utils/Constants";
import moment from "moment";
import DistributionGroupTooltip from "modules/_shared/distribution-group-tooltip";
import {
  CircleMinusIcon,
  PencilIcon,
  PeopleOutlineIcon,
  RestoreIcon,
  InfoIcon,
} from "modules/icons";
import DistributionGroupName from "modules/_shared/distribution-group-name";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  tagId: string;
}

const AdminTagDrawaer = ({ tagId }: IProps) => {
  const { openModal, closeDrawer } = useOverlay();
  const { tagsById, tagCategoriesById } = useTags();
  const { distributionGroups } = useDistributionGroups();

  const onEditClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_EDIT_TAG,
      id: tagId,
    });
  }, [openModal, tagId]);

  const onArchiveTagClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CONFIRM_ARCHIVE_TAG,
      ids: [tagId],
    });
  }, [openModal, tagId]);

  const onUnarchiveTagClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_CONFIRM_UNARCHIVE_TAG,
      ids: [tagId],
    });
  }, [openModal, tagId]);

  const tag = useMemo(() => tagsById[tagId], [tagId, tagsById]);

  const distributionGroupList = useMemo(
    () => distributionGroups.filter((obj) => obj.tagIdList.includes(tagId)),
    [distributionGroups, tagId],
  );

  useEffect(() => {
    if (tagsById[tagId] === null) {
      closeDrawer();
    }
  }, [closeDrawer, tagId, tagsById]);

  if (!tag) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader>
        <Center fontWeight="bold">Tag Details</Center>
        <Center height={10}>
          <Divider />
        </Center>
      </DrawerHeader>
      <DrawerBody>
        <VStack w="full" alignItems="flex-start">
          <HStack w="full" spacing={0}>
            <Text
              fontSize={28}
              fontWeight={700}
              fontStyle={tag.active ? "normal" : "italic"}
              color={tag.active ? colors.black : colors.gray30}
            >
              {HASH_KEY}
              {tag.parentTagId && `${tagsById[tag.parentTagId]?.name}-`}
              {tag.name}
            </Text>
          </HStack>
          <HStack w="full" pt={6}>
            <InfoIcon boxSize={5} />
            <Text fontWeight={700}>Info</Text>
            <Spacer />
            {tag.active && (
              <Button
                variant="ghost"
                color={colors.orangePrimary}
                onClick={onEditClicked}
                leftIcon={<PencilIcon fill={colors.orangePrimary} />}
              >
                Edit
              </Button>
            )}
          </HStack>
          <Divider borderColor={colors.black} />
          {tag.tagCategoryId && (
            <HStack w="full">
              <Text fontSize={14} fontWeight={600} w={200}>
                Category
              </Text>
              <Text>{tagCategoriesById[tag.tagCategoryId]?.name}</Text>
            </HStack>
          )}
          <HStack w="full">
            <Text fontSize={14} fontWeight={600} w={200}>
              Parent Tag
            </Text>
            <Text>
              {tag.parentTagId ? tagsById[tag.parentTagId]?.name : "--"}
            </Text>
          </HStack>
          <HStack w="full">
            <Text fontSize={14} fontWeight={600} w={200}>
              Created
            </Text>
            <Text fontSize={14} color={colors.gray50}>
              {moment(tag.createTime).format(MONTH_DAY_YEAR_FORMAT)}
            </Text>
          </HStack>
          {tag.active === false && tag.inactiveTime !== null && (
            <HStack w="full">
              <Text fontSize={14} fontWeight={600} w={200}>
                Archived
              </Text>
              <Text fontSize={14} color={colors.gray50}>
                {moment(tag.inactiveTime).format(MONTH_DAY_YEAR_FORMAT)}
              </Text>
            </HStack>
          )}
          <Divider />
          <HStack w="full">
            {tag.active ? (
              <Button
                height={5}
                p={0}
                m={0}
                leftIcon={<CircleMinusIcon boxSize={5} fill={colors.gray40} />}
                onClick={onArchiveTagClicked}
                variant="ghost"
                color={colors.gray40}
                w="fit-content"
                alignSelf="flex-end"
                fontSize={12}
              >
                Archive Tag
              </Button>
            ) : (
              <Button
                height={5}
                p={0}
                m={0}
                leftIcon={<RestoreIcon boxSize={5} fill={colors.gray40} />}
                onClick={onUnarchiveTagClicked}
                variant="ghost"
                color={colors.gray40}
                w="fit-content"
                alignSelf="flex-end"
                fontSize={12}
              >
                Unarchive Tag
              </Button>
            )}
          </HStack>

          {distributionGroupList.length > 0 && (
            <Box w="full" pt={6}>
              <HStack>
                <PeopleOutlineIcon fill={colors.blackSecondary} boxSize={5} />
                <Text fontWeight={700}>
                  Associated Groups ({distributionGroupList.length})
                </Text>
              </HStack>
              <Divider borderColor={colors.black} />
              <Text fontSize={11} color={colors.grayMain} pb={2} pt={1}>
                Groups that use this tag
              </Text>
              {distributionGroupList.map((distributionGroup) => (
                <Box
                  key={distributionGroup.id}
                  h="32px"
                  mb={3}
                  borderBottom="1px solid"
                  borderBottomColor={colors.gray0}
                >
                  <DistributionGroupTooltip id={distributionGroup.id}>
                    <Text
                      fontSize={12}
                      fontWeight={700}
                      color={
                        distributionGroup.active
                          ? colors.blue
                          : colors.grayPrimary
                      }
                    >
                      <DistributionGroupName
                        distributionGroupId={distributionGroup.id}
                      />
                    </Text>
                  </DistributionGroupTooltip>
                </Box>
              ))}
            </Box>
          )}
        </VStack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AdminTagDrawaer);
